import { Box, Button, Grid, Hidden, IconButton, Skeleton, Tab, TabProps, Tabs, Theme, Typography, useTheme } from "@mui/material";
import { useHistory, useLocation } from "react-router";
import { CustomeModalButton } from "../../../lib/formik/NewFormInputs";
import { CSSObject, maxWidth, SxProps } from "@mui/system";
import { ChartIcon, CurrencyIcon, HideIcon, MoneyGrow, PercentageIcons, TotalInvestors, ViewIcon, TotalSipsIcon } from "../../../assets/SvgIcons";
import { useEffect, useState } from "react";
import useQuery from "../../../utils/useQuery";
import OverView from "./OverView";
import Mandates from "./mandates";
import Holdings from "./holdings";
import Orders from "./orders";
import Recommendations from "./recommendations";
import { useDispatch, useSelector } from "react-redux";
import {
    setAppliedFundsFilters,
    setAppliedHoldingsFilters,
    setAppliedSystematicPlansFilters,
    setCheckedHoldingSchemes,
    setExploreMutualFundsPageVisited,
    setHoldingsActiveCategory,
    setSelectedAllHoldingsInfo,
    setSelectedHoldingsIds,
    setSelectedHoldingsInfo,
    setSelectedSchemesObj,
    setSystematicPlansSearchText
} from "../../../redux-store/actions/investorProfile";
import BreadCrum from "../../../lib/BreadCrum";
import { RootStateType } from "../../../redux-store/reducers";
import { navigationData } from "../types";
import { CategoryWiseDistibutionRes, TotalCardValuesRes, GetSipSummaryRes } from "../../../redux-store/types/products";
import { getCategoryWiseDistribution, totalCardValuesApi, GetSummaryApi } from "../../../redux-store/actions/product";
import { USER_ROLES } from "../../../utils/constant";
import { formatCurrency, isFamilyView, isRMLikeRole} from "../../../utils/utilityFunctions";
import { ZeroStateInvestment } from "../common/ZeroStateInvestment";
import SystematicPlans from "./systematicPlans";
import { TabContext } from "@mui/lab";

const tabsList = ['Overview', 'Holdings', 'Orders', 'Recommendations', 'Manage Mandates', 'Systematic Plans']

export default function MutualFund(): JSX.Element {
    const dispatch = useDispatch()
    const location = useLocation()
    const query = useQuery()
    const { palette, typography } = useTheme()
    const history = useHistory();
    const { pathname: pathName, state } = history.location

    const {
        role = '',
    } = useSelector((store: RootStateType) => store.auth);
    const {
        selectedPortfolio,
        portfolioList,
        isPortfolioHeldaway
    } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { iwappRmId, investorClientId, isPortfolioAvailable = false, showMfcReport, familyMemberId = "", viewAsFamilyId = "" } = useSelector((store: RootStateType) => store.id)
    const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData);
    const {
        selectAllHoldingsInfo
    } = useSelector((store: RootStateType) => store.investorProfile);
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const navState = state as navigationData

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSipLoading, setIsSipLoading] = useState<boolean>(false);
    const [selectTab, setSelectTab] = useState(query.get('tab') || 'Overview')
    const [cardValues, setCardValues] = useState<TotalCardValuesRes>({} as TotalCardValuesRes)
    const [portfolioDoesntExist, setPortfolioDoesntExist] = useState<boolean>(false);
    const [zeroStateCheck, setZeroStateCheck] = useState<boolean>(false);
    const [maskedData, setMaskedData] = useState<boolean>(false);

    const currentTab = query.get('tab');
    const [urlPortfolio, setUrlPortfolio] = useState(currentTab == "Overview" ? (query.get('portfolio') ??
        "") : (selectedPortfolio?.value ?? ""));
    const invClientCode = isRMLikeRole(role) ? navState?.clientCode : isFamilyView(pathName) ? viewAsFamilyId : investorClientId;
      const [sipSummary, setSipSummary] = useState<GetSipSummaryRes>({} as GetSipSummaryRes);
    const totalCardValuesInvestorLevel = async () => {
        try {
            setIsLoading(true);

            const cardValuesRes = await (dispatch(totalCardValuesApi({
                rmId: `${investorRmId}`,
                category: '',
                clientCode: invClientCode,
                portfolioId: urlPortfolio,
                assetGroup: "MF"
            })) as unknown as Promise<TotalCardValuesRes>)

            if (cardValuesRes?.isSuccess) {
                setCardValues(cardValuesRes)
                setZeroStateCheck(false);

                if (cardValuesRes?.result?.totalInvestedValue === 0) {
                    setPortfolioDoesntExist(true)
                } else {
                    setPortfolioDoesntExist(false)
                }
            } else {
                setZeroStateCheck(true);
                setCardValues({} as TotalCardValuesRes)
            }

        } catch (e) {
            console.log("Error", e);
        } finally {
            setIsLoading(false);
        }
    }


    // sipSummaryInvestorLevel is declared below but referenced here safely:
    // useEffect callbacks execute after render (not at parse time), so the const
    // is already initialized by the time this effect runs — no temporal dead zone risk.
    useEffect(() => {
        totalCardValuesInvestorLevel();
        sipSummaryInvestorLevel();
    }, [urlPortfolio])

    const sipSummaryInvestorLevel = async () => {
        try {
            setIsSipLoading(true);
            const sipRes = await (dispatch(GetSummaryApi({
                rmId: `${investorRmId}`,
                clientCode: invClientCode,
                portfolioId: urlPortfolio,
                assetGroup: "MF",
            })) as unknown as Promise<GetSipSummaryRes>)

            if (sipRes?.isSuccess) {
                setSipSummary(sipRes)
            } else {
                setSipSummary({} as GetSipSummaryRes)
            }

        } catch (e) {
            console.log("Error fetching SIP summary", e);
        } finally {
            setIsSipLoading(false);
        }
    }


    const handleChangeTab = (event: React.SyntheticEvent, value: string) => {
        setSelectTab(value)
        setUrlPortfolio(selectedPortfolio?.value ?? "")

        if (value === "Systematic Plans") {
            dispatch(setSystematicPlansSearchText(""))
            dispatch(setAppliedSystematicPlansFilters([]))

            history.replace({
                pathname: location.pathname, search: `?tab=${value}&type=SIP`, state: {
                    clientCode: navState?.clientCode ?? ""
                }
            })
        } else if (value === "Overview") {
            history.replace({
                pathname: location.pathname, search: `?tab=${value}&portfolio=${selectedPortfolio?.value ?? ""}`, state: {
                    clientCode: navState?.clientCode ?? ""
                }
            })
        }
        else {
            history.replace({
                pathname: location.pathname, search: `?tab=${value}`, state: {
                    clientCode: navState?.clientCode ?? ""
                }
            })
        }
    }

    const handleExploreMutualFunds = () => {

        dispatch(setSelectedAllHoldingsInfo([]))

        dispatch(setAppliedFundsFilters([]));
        dispatch(setSelectedSchemesObj({}));
        dispatch(setExploreMutualFundsPageVisited(false));

        dispatch(setAppliedHoldingsFilters([]))
        dispatch(setHoldingsActiveCategory("All"))
        dispatch(setSelectedHoldingsIds([]))
        dispatch(setCheckedHoldingSchemes([]))
        dispatch(setSelectedHoldingsInfo([]))

        dispatch(setSystematicPlansSearchText(""))
        dispatch(setAppliedSystematicPlansFilters([]))
        history.push(`${pathName}/explore`, {
            clientCode: navState?.clientCode || "",
            // hideCheckBox: false
        })
    }

    const handleEcasDownloads = () => {
        history.push(`${pathName}/ecas-reports`, {
            clientCode: navState?.clientCode ?? ""
        })
    }

    const handleChangeUrlPortfolio = (event: React.SyntheticEvent, portfolioValue: string) => {
        setUrlPortfolio(portfolioValue)

        history.replace({
            pathname: location.pathname,
            search: `?tab=Overview&portfolio=${portfolioValue ?? ""}`,
            state: { clientCode: navState?.clientCode ?? "" }
        })
    }

    const initialRouter = pathName.split('/').filter((item) => item !== '')

    // console.log('path', initialRouter, pathName)

    const rmRouterPath = [
        {
            name: 'Dashboard',
            path: '/dashboard',
            warning: selectTab === "Holdings" && selectAllHoldingsInfo.length > 0,
            handleClick: () => {
                dispatch(setSelectedAllHoldingsInfo([]))
            }
        },
        {
            name: `${initialRouter[1]} ${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode: navState?.clientCode || ""
            },
            warning: selectTab === "Holdings" && selectAllHoldingsInfo.length > 0,
            handleClick: () => {
                dispatch(setSelectedAllHoldingsInfo([]))
            }
        },
        {
            name: 'Detailed Portfolio Holdings',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode: navState?.clientCode || ""
            },
            search: `tab=Overview&portfolio=${selectedPortfolio?.value ?? ""}`
        }
    ]

    const investorRouterPath = [
        ...(familyMemberId
            ?
            [{
                name: 'View Family',
                path: `/investor-family/family-profile/${familyMemberId}`,
            }, {
                name: `${initialRouter[1]}`,
                path: `/${initialRouter[0]}/${initialRouter[1]}`,
            }]
            : [{
                name: `Dashboard ${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
                path: '/investor-dashboard',
                warning: selectTab === "Holdings" && selectAllHoldingsInfo.length > 0,
                handleClick: () => {
                    dispatch(setSelectedAllHoldingsInfo([]))
                }
            }]),
        {
            name: 'Detailed Portfolio Holdings',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode: navState?.clientCode || ""
            },
            search: `tab=Overview&portfolio=${selectedPortfolio?.value ?? ""}`
        }
    ]


    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: {
                        xs: 'column',
                        sm: 'row'
                    },
                    flexWrap: "nowrap",
                    rowGap: "15px"
                }}
            >
                <Box sx={{ flex: 1 }}>
                    <BreadCrum array={isRMLikeRole(role) ? rmRouterPath : investorRouterPath} />
                    {currentTab === "Overview" &&
                        (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: 2 }}>
                                <Typography
                                    variant="body_600"
                                    color={palette.text.primary}
                                    sx={{ p: 1 }}>
                                    Privacy Mode
                                </Typography>
                                {maskedData
                                    ?
                                    <IconButton
                                        aria-label="Show masked data"
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'transparent'
                                            }
                                        }}
                                        onClick={() => setMaskedData(!maskedData)}
                                    >
                                        <HideIcon />
                                    </IconButton>
                                    :
                                    <IconButton
                                        aria-label="Hide sensitive data"
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'transparent'
                                            }
                                        }}
                                        onClick={() => setMaskedData(!maskedData)}
                                    >
                                        <ViewIcon />
                                    </IconButton>
                                }
                            </Box>
                        )
                    }
                </Box>
                {/* <br /> */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: {
                            xs: 'space-between',
                            sm: 'flex-end',
                        },
                        width: '100%',
                        alignItems: 'center',
                    }}>
                    {/* <Hidden smUp={true}>
                        <Typography variant="overline_600" fontSize={'16px'} color={palette.text.primary}>{selectTab}</Typography>
                    </Hidden> */}
                    {!isPortfolioHeldaway && (
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "25px"
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={handleEcasDownloads}
                                disabled={isFamilyView(pathName)}
                                sx={(theme: Theme) => ({
                                    ...theme.typography.subtitle_300,
                                    fontWeight: 700,
                                    borderRadius: "10px",
                                    border: `1px solid ${theme.palette.primary[500]}`,
                                    color: theme.palette.primary[500],
                                    padding: { xs: "6px 10px", sm: "7px 10px", md: "9px 15px" },
                                    "&.Mui-disabled": {
                                        backgroundColor: `${theme.palette.text.secondary}B2`,
                                        color: theme.palette.text.secondary,
                                    },
                                })}
                            >
                                Import External Funds
                            </Button>
                            <Button
                                onClick={handleExploreMutualFunds}
                                variant="contained"
                                sx={(theme: Theme) => ({
                                    ...theme.typography.subtitle_300,
                                    fontWeight: 700,
                                    borderRadius: "10px",
                                    padding: "10px 15px",
                                    backgroundColor: theme.palette.primary[500],
                                    color: theme.palette.textDark.primary,
                                    maxWidth: '250px',
                                    "&.Mui-disabled": {
                                        backgroundColor: `${theme.palette.text.secondary}B2`,
                                        color: theme.palette.text.secondary,
                                    },
                                    flexWrap: 'nowrap',
                                })}
                                disabled={[USER_ROLES.INVESTOR].includes(role) && !isPortfolioAvailable || isFamilyView(pathName)}
                            >
                                Explore <Hidden only={'xs'}> Mutual Funds</Hidden>
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            <Hidden only={'xs'}>
                <Grid container spacing={2} mt={'25px'}>
                    <Grid item width={'100%'} sm={6} lg={3}>
                        <Card
                            Icons={MoneyGrow}
                            title="Market Value"
                            percent={false}
                            value={cardValues?.result?.totalMarketValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalMarketValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalMarketValue ?? 0, locale, currencyCode })}`}
                            isLoading={isLoading}
                            maskedData={maskedData}
                        />
                    </Grid>
                    <Grid item width={'100%'} sm={6} lg={3}>
                        <Card
                            Icons={CurrencyIcon}
                            title="Invested Value"
                            percent={false}
                            value={cardValues?.result?.totalInvestedValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalInvestedValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalInvestedValue ?? 0, locale, currencyCode })}`}
                            isLoading={isLoading}
                            maskedData={maskedData}
                        />
                    </Grid>
                    <Grid item width={'100%'} sm={6} lg={3}>
                        <Card
                            Icons={ChartIcon}
                            title="Gain/Loss (%)"
                            value={cardValues?.result?.gainLoss < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.gainLoss ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.gainLoss ?? 0, locale, currencyCode })}`}
                            percent
                            percentageValue={cardValues?.result?.percentage ?? 0}
                            color={Number(cardValues?.result?.gainLoss ?? 0) >= 0 ? palette.success[500] : palette.error[500]}
                            isLoading={isLoading}
                            maskedData={maskedData}
                        />
                    </Grid>
                    <Grid item width={'100%'} sm={6} lg={3}>
                        <Card
                            Icons={PercentageIcons}
                            value={!Number.isNaN(Number(cardValues?.result?.irr)) ?
                                `${Math.round(cardValues?.result?.irr * 100) / 100} %` : '0%'}
                            title="Absolute Return %"
                            percent={false}
                            isLoading={isLoading}
                            color={Number(cardValues?.result?.irr ?? 0) >= 0 ? palette.success[500] : palette.error[500]}
                            maskedData={maskedData}
                        />
                        </Grid>
                        <Grid item width={'100%'} sm={6} lg={3}>
                        <Card
                            Icons={TotalSipsIcon}
                            title="Active SIPs (#)"
                            percent={false}
                            value={`${sipSummary?.result?.activeCount ?? 0}`}
                            isLoading={isSipLoading}
                            maskedData={maskedData}
                        />
                    </Grid>
                    <Grid item width={'100%'} sm={6} lg={3}>
                        <Card
                            Icons={CurrencyIcon}
                            title="Active SIP Amount"
                            percent={false}
                            value={sipSummary?.result?.sipAmount < 0 ? `- ${formatCurrency({ value: Math.abs(sipSummary?.result?.sipAmount ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: sipSummary?.result?.sipAmount ?? 0, locale, currencyCode })}`}
                            isLoading={isSipLoading}
                            maskedData={maskedData}
                        />
                    </Grid>
                    <Grid item width={'100%'} sm={6} lg={3}>
                        <Card
                            Icons={MoneyGrow}
                            title="Total Invested SIP Amount"
                            percent={false}
                            value={sipSummary?.result?.totalSipAmount < 0 ? `- ${formatCurrency({ value: Math.abs(sipSummary?.result?.totalSipAmount ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: sipSummary?.result?.totalSipAmount ?? 0, locale, currencyCode })}`}
                            isLoading={isSipLoading}
                            maskedData={maskedData}
                        />

                    </Grid>
                </Grid>
            </Hidden>
            <Hidden smUp={true}>
                <Box sx={{ width: '100%', borderRadius: '15px', flexDirection: 'column', background: palette.background.paper, padding: '20px 18px', gap: '25px', mt: '30px' }}>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                        <ResponsiveCard
                            title="Market Value"
                            value={cardValues?.result?.totalMarketValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalMarketValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalMarketValue ?? 0, locale, currencyCode })}`}
                            maskedData={maskedData}
                        />
                        <ResponsiveCard
                            title="Invested Value"
                            value={cardValues?.result?.totalInvestedValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalInvestedValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalInvestedValue ?? 0, locale, currencyCode })}`}
                            align="right"
                            textAlign="right"
                            maskedData={maskedData}
                        />
                    </Box>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '25px' }}>
                        <ResponsiveCard
                            title="Gain/Loss (%)"
                            value={cardValues?.result?.gainLoss < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.gainLoss ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.gainLoss ?? 0, locale, currencyCode })}`}
                            color={Number(cardValues?.result?.gainLoss ?? 0) >= 0 ? palette.success[500] : palette.error[500]}
                            maskedData={maskedData}

                        />
                        <ResponsiveCard
                            title="Absolute Return %"
                            value={!Number.isNaN(Number(cardValues?.result?.irr)) ?
                                `${Math.round(cardValues?.result?.irr * 100) / 100} %` : '0%'}
                            color={Number(cardValues?.result?.irr ?? 0) >= 0 ? palette.success[500] : palette.error[500]}
                            align="right"
                            textAlign="right"
                            maskedData={maskedData}
                        />
                    </Box>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '25px' }}>
                        <ResponsiveCard
                            title="Active SIPs (#)"
                            value={`${sipSummary?.result?.activeCount ?? 0}`}
                            maskedData={maskedData}
                        />
                        <ResponsiveCard
                            title="Active SIP Amount"
                            value={sipSummary?.result?.sipAmount < 0 ? `- ${formatCurrency({ value: Math.abs(sipSummary?.result?.sipAmount ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: sipSummary?.result?.sipAmount ?? 0, locale, currencyCode })}`}
                            align="right"
                            textAlign="right"
                            maskedData={maskedData}
                        />
                        </Box>
                     <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '25px' }}>
                      <ResponsiveCard
                            title="Total Invested SIP Amount*"
                            value={sipSummary?.result?.totalSipAmount < 0 ? `- ${formatCurrency({ value: Math.abs(sipSummary?.result?.totalSipAmount ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: sipSummary?.result?.totalSipAmount ?? 0, locale, currencyCode })}`}
                            align="left"
                            textAlign="left"
                            maskedData={maskedData}
                        />
                    </Box>
                </Box>
            </Hidden>
            {/* <Box mt={'45px'} sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                gap: '10px',
                paddingBottom: '15px',
                borderBottom: `1px solid #CDCDCD`,
                overflowX: 'auto',
                width: '100%',
                '&::-webkit-scrollbar': {
                    display: 'none'
                }
            }}>
                {tabsList.map((item, index) => {
                    return (
                        <Button
                            variant={selectTab === item ? "contained" : "text"}
                            key={item}
                            onClick={() => handleChangeTab(item)}
                            sx={(theme: Theme) => ({
                                ...typography.body_500,
                                padding: '5px 15px',
                                background: selectTab === item ? theme.palette.primary[500] : 'transparent',
                                borderRadius: '7px',
                                outline: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: selectTab === item ? theme.palette.textDark.primary : theme.palette.text.secondary,
                                textWrap: 'nowrap'
                            })}
                        >
                            {item}
                        </Button>
                    )
                })}
            </Box> */}
            <TabContext value={selectTab}>
                <Tabs
                    value={selectTab}
                    onChange={handleChangeTab}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    TabIndicatorProps={{
                        sx: {
                            display: 'none',

                        }
                    }}
                    aria-label="scrollable auto tabs example"
                    sx={(theme: Theme) => ({
                        mt: '45px',
                        borderBottom: `1px solid ${theme.palette.customColors.pastelGrey}`,
                        paddingBottom: '15px',
                    })}
                >
                    {tabsList.map((item) => {
                        return (
                            <Tab
                                key={item}
                                label={item}
                                value={item}
                                sx={(theme: Theme) => ({
                                    ...typography.body_500,
                                    lineHeight: '14px',
                                    textTransform: 'capitalize',
                                    '&.MuiTab-root ': {
                                        minHeight: '10px'
                                    },
                                    '&.Mui-selected': {
                                        color: theme.palette.textDark.primary,
                                        background: theme.palette.primary[500],
                                        borderRadius: '7px',
                                    },
                                })}
                                disabled={isLoading}
                            />
                        )
                    })}
                </Tabs>
            </TabContext>
            <Box mt={'25px'}>
                {selectTab === "Overview" && (
                    <Box>
                        <TabContext
                            value={urlPortfolio}
                        >
                            <Tabs
                                value={urlPortfolio}
                                onChange={handleChangeUrlPortfolio}
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                                TabIndicatorProps={{
                                    sx: { display: 'none' }
                                }}
                                sx={{
                                    paddingBottom: '15px',
                                }}
                            >
                                {(portfolioList ?? []).map((item, index) => {
                                    return (
                                        <Tab
                                            key={`${item?.value ?? ""}-${index}`}
                                            label={item?.label ?? ""}
                                            value={item?.value ?? ""}
                                            sx={(theme: Theme) => ({
                                                ...typography.body_500,
                                                lineHeight: '14px',
                                                textTransform: 'capitalize',
                                                '&.MuiTab-root ': {
                                                    minHeight: '10px'
                                                },
                                                '&.Mui-selected': {
                                                    color: theme.palette.textDark.primary,
                                                    background: theme.palette.primary[500],
                                                    borderRadius: '7px',
                                                },
                                            })}
                                            disabled={isLoading}
                                        />
                                    )
                                })}
                            </Tabs>
                        </TabContext>
                        {
                            (!isLoading && (zeroStateCheck || portfolioDoesntExist)) ? (
                                <Box
                                    sx={(theme: Theme) => ({
                                        backgroundColor: theme.palette.common.white,
                                        borderRadius: "25px",
                                        padding: "25px 30px",
                                        height: "50vh"
                                    })}
                                >
                                    <ZeroStateInvestment isSuccess={portfolioDoesntExist} />
                                </Box>
                            ) : (
                                <OverView
                                    key={urlPortfolio}
                                    clientCode={navState?.clientCode}
                                    loading={isLoading}
                                    urlPortfolio={urlPortfolio}
                                    maskedData={maskedData}
                                />
                            )
                        }
                    </Box>
                )}
                {selectTab === "Holdings" && (!isLoading && (zeroStateCheck || portfolioDoesntExist) ?
                    <Box
                        sx={(theme: Theme) => ({
                            backgroundColor: theme.palette.common.white,
                            borderRadius: "25px",
                            padding: "25px 30px",
                            height: "50vh"
                        })}
                    >
                        <ZeroStateInvestment isSuccess={portfolioDoesntExist} />
                    </Box> :
                    <Holdings clientCode={navState?.clientCode} />
                )}
                {selectTab === 'Orders' && <Orders clientCode={navState?.clientCode} />}
                {selectTab === 'Recommendations' && <Recommendations clientCode={navState?.clientCode} />}
                {selectTab === 'Manage Mandates' && <Mandates clientCode={navState?.clientCode} />}
                {selectTab === "Systematic Plans" && <SystematicPlans clientCode={navState?.clientCode ?? ""} />}
            </Box>
        </Box >
    )
}

type CardProps = {
    Icons: (props: React.SVGProps<SVGSVGElement>) => JSX.Element
    title: string
    value: string
    color?: string
    percent?: boolean
    percentageValue?: number
    isLoading?: boolean
    maskedData?: boolean
}

export const Card = (props: CardProps) => {
    const { Icons, title, value, color, percent, percentageValue, isLoading, maskedData } = props
    const { palette, typography } = useTheme()
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px', borderRadius: '15px', padding: '0px 18px', height: '102px', background: palette.common.white, boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)' }} >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Icons />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '10px', width: '80%', }}>
                {isLoading ? <>
                    <Skeleton animation="wave" sx={{ width: '60%' }} />
                    <Skeleton animation="wave" sx={{ width: '100%' }} />
                </> : <>
                    <Typography variant="subtitle_500" color={palette.text.primary} sx={{ lineHeight: '14px' }}>{title}</Typography>
                    {maskedData
                        ?
                        <Typography variant="body_600" color='text.primary'>
                            ****
                        </Typography>
                        :
                        !percent ? <Typography variant="h3" color={color ? color : palette.text.primary}>{value}</Typography> : <Typography variant="h3" color={color ? color : palette.text.primary}>{`${value} (${percentageValue ? percentageValue.toFixed(2) : '0'}%)`}</Typography>
                    }

                </>}
            </Box>
        </Box>
    )
}
// #AlignIssued

export const ResponsiveCard = (props: { align?: 'left' | 'right', textAlign?: 'left' | 'right', title: string, value: string, color?: string, maskedData?: boolean }) => {
    const { palette, typography } = useTheme()
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: props.align === 'right' ? 'flex-end' : 'flex-start' }}>
            <Typography variant="subtitle_600" align={props.align ? props.align : 'left'} color={palette.text.secondary}>{props.title}</Typography>
            {props?.maskedData
                ? (
                    <Typography variant="body_600" color='text.primary' align={props.textAlign || props.align || 'left'}>
                        ****
                    </Typography>
                ) : (
                    <Typography variant="body_500" color={props.color || palette.text.primary} align={props.textAlign || props.align || 'left'}>{props.value}</Typography>
                )}
        </Box>
    )
}